import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import {
  clearAuthSession,
  getStoredUser,
  isUserLoggedIn,
} from '../utils/authSession';
import { storage } from '../utils/storage';

export default function LoggedInGate({ children }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [userName, setUserName] = useState('');

  const refresh = useCallback(async () => {
    setStatus('loading');
    const loggedIn = await isUserLoggedIn();
    if (!loggedIn) {
      setStatus('guest');
      setUserName('');
      return;
    }
    const user = await getStoredUser();
    const name = user?.name ?? (await storage.getItem('userName')) ?? '';
    setUserName(name);
    setStatus('loggedIn');
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleLogout = async () => {
    await clearAuthSession();
    window.dispatchEvent(new Event('profile-updated'));
    navigate('/', { replace: true });
  };

  if (status === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (status === 'loggedIn') {
    const displayName = userName || t('Account');
    return (
      <Paper elevation={0} variant="outlined" sx={{ p: { xs: 4, sm: 5 }, borderRadius: 4, textAlign: 'center' }}>
        <Avatar
          sx={{
            width: 80,
            height: 80,
            mx: 'auto',
            mb: 2,
            fontSize: 32,
            fontWeight: 800,
            bgcolor: 'primary.main',
          }}
        >
          {displayName.charAt(0).toUpperCase()}
        </Avatar>
        <Typography variant="h6" fontWeight={800} gutterBottom>
          {t('Welcome')}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {t('already_logged_in', { name: displayName })}
        </Typography>
        <Stack spacing={1.5}>
          <Button
            component={RouterLink}
            to="/profile"
            variant="contained"
            size="large"
            startIcon={<PersonOutlinedIcon />}
            sx={{ borderRadius: 2.5, py: 1.25 }}
          >
            {t('go_to_profile')}
          </Button>
          <Button
            component={RouterLink}
            to="/"
            variant="outlined"
            size="large"
            startIcon={<HomeOutlinedIcon />}
            sx={{ borderRadius: 2.5, py: 1.25 }}
          >
            {t('categories')}
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="large"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{ borderRadius: 2.5, py: 1.25 }}
          >
            {t('logout')}
          </Button>
        </Stack>
      </Paper>
    );
  }

  return children;
}
